import mapboxgl from 'mapbox-gl';
import {
  ActionSheetController,
  IonicPage,
  NavController,
  NavParams,
} from 'ionic-angular';
import { Component, OnDestroy } from '@angular/core';

import { DirectionsService } from '../../providers/directions/directions';
import { MapProvider, MapStyle } from './../../providers/map/map';

@IonicPage()
@Component({
  selector: 'page-order-navigation',
  templateUrl: 'order-navigation.html',
})
export class OrderNavigationPage implements OnDestroy {
  readonly MAP_CONTAINER_ID = 'order-navigation-map';
  readonly ADDRESS_MARKER_ICON = 'assets/images/map-marker-flag.png';
  readonly PARKING_MARKER_ICON = 'assets/images/car-pin.png';
  readonly GEOLOCATION_MARKER_ICON = 'assets/images/geo-pin.png';
  readonly CURRENT_POSITION_MARKER_ICON =
    'assets/images/marker-current-location.svg';
  readonly ACTION_SHEET_TITLE =
    'You are getting directions to the the street address on the order. The user has pinned their specific parking spot. Be sure to refer back to this pin upon arrival to the address.';

  map: any;
  initialCoordinates: any = new mapboxgl.LngLat(-121, 37);
  parkingCoords: any;
  geolocationCoords: any;
  geolocationCreated: Date;
  deliveryNote: string;
  addressCoordinates: any;
  mapBounds: any = new mapboxgl.LngLatBounds();
  mapStyle: MapStyle;

  addressMarker: any;
  parkingSpotMarker: any;
  geolocationMarker: any;
  currentPositionMarker: any;
  markersArray: any = [];
  private currentPositionWatchId: number = null;

  constructor(
    public navCtrl: NavController,
    public navParams: NavParams,
    private mapService: MapProvider,
    private actionSheet: ActionSheetController,
    private directions: DirectionsService
  ) {
    this.deliveryNote = this.navParams.get('deliveryNote');
    const addressInput = this.navParams.get(
      'addressCoordinates'
    ) as google.maps.LatLng;
    this.addressCoordinates = new mapboxgl.LngLat(
      addressInput.lng(),
      addressInput.lat()
    );

    const parkingInput = this.navParams.get(
      'parkingCoords'
    ) as google.maps.LatLng;
    this.parkingCoords = new mapboxgl.LngLat(
      parkingInput.lng(),
      parkingInput.lat()
    );

    const geolocationInput = this.navParams.get(
      'geolocationCoords'
    ) as google.maps.LatLng;

    if (geolocationInput) {
      this.geolocationCoords = new mapboxgl.LngLat(
        geolocationInput.lng(),
        geolocationInput.lat()
      );
    }

    this.geolocationCreated = this.navParams.get('geolocationCreated');
  }

  ionViewDidLoad() {
    this.initMap();
    this.map.once('idle', () => {
      this.drawAddressMarker();
      this.drawParkingSpotMarker();
      this.drawGeolocationMarker();
      this.extendAndFitMapBounds();
      this.watchCurrentPosition();
    });
  }

  ngOnDestroy() {
    if (
      this.currentPositionWatchId !== null &&
      typeof navigator !== 'undefined' &&
      navigator.geolocation
    ) {
      navigator.geolocation.clearWatch(this.currentPositionWatchId);
    }
  }

  private initMap() {
    const coords = this.addressCoordinates || this.initialCoordinates;
    this.mapStyle = MapStyle.SatelliteWithData;
    this.map = this.mapService.createMBMap(this.MAP_CONTAINER_ID, {
      style: this.mapStyle,
      center: coords,
      zoom: 20,
    });
  }

  private drawAddressMarker() {
    this.addressMarker = this.mapService
      .drawMPMarker({
        iconUrl: this.ADDRESS_MARKER_ICON,
        coords: this.addressCoordinates,
        map: this.map,
        height: 64,
        width: 64,
      })
      .setOffset([0, -32]);
    this.markersArray.push(this.addressMarker);
  }

  private drawParkingSpotMarker() {
    this.parkingSpotMarker = this.mapService
      .drawMPMarker({
        coords: this.parkingCoords,
        map: this.map,
        iconUrl: this.PARKING_MARKER_ICON,
        width: 50,
        height: 67,
      })
      .setOffset([0, -30]);
    this.markersArray.push(this.parkingSpotMarker);
  }

  private drawGeolocationMarker() {
    if (this.geolocationCoords) {
      this.geolocationMarker = this.mapService
        .drawMPMarker({
          iconUrl: this.GEOLOCATION_MARKER_ICON,
          coords: this.geolocationCoords,
          width: 50,
          height: 67,
          map: this.map,
          timestamp: this.geolocationCreated,
        })
        .setOffset([0, -30]);
      this.markersArray.push(this.geolocationMarker);
    }
  }

  private watchCurrentPosition() {
    if (
      typeof navigator === 'undefined' ||
      !navigator.geolocation ||
      !navigator.geolocation.watchPosition
    ) {
      return;
    }

    this.currentPositionWatchId = navigator.geolocation.watchPosition(
      position => this.drawCurrentPositionMarker(position),
      error => console.log('error receiving current position', error),
      {
        enableHighAccuracy: true,
        maximumAge: 0,
        timeout: 10000,
      }
    );
  }

  private drawCurrentPositionMarker(position: any) {
    const coords = new mapboxgl.LngLat(
      position.coords.longitude,
      position.coords.latitude
    );

    if (!this.currentPositionMarker) {
      this.currentPositionMarker = this.mapService.drawMPMarker({
        iconUrl: this.CURRENT_POSITION_MARKER_ICON,
        coords,
        map: this.map,
        height: 64,
        width: 64,
      });
      this.markersArray.push(this.currentPositionMarker);
      this.extendAndFitMapBounds();
      return;
    }

    this.currentPositionMarker.setLngLat(coords);
  }

  private extendAndFitMapBounds() {
    this.mapBounds = new mapboxgl.LngLatBounds();
    this.markersArray.forEach(marker => {
      this.mapBounds.extend(marker.getLngLat());
    });
    this.map.fitBounds(this.mapBounds, { padding: 100, maxZoom: 18 });
  }

  protected toggleMapViewMode() {
    const newStyle =
      this.mapStyle === MapStyle.SatelliteWithData
        ? MapStyle.RoadmapWithNoData
        : MapStyle.SatelliteWithData;
    this.mapStyle = newStyle;
    this.map.setStyle(this.mapStyle);
  }

  didTapNavigate() {
    this.actionSheet
      .create({
        title: this.ACTION_SHEET_TITLE,
        buttons: [
          { text: 'Navigate me', handler: () => this.openNavigation() },
          { text: 'Cancel', role: 'cancel' },
        ],
      })
      .present();
  }

  openNavigation() {
    this.directions.get(
      this.addressCoordinates.lat,
      this.addressCoordinates.lng
    );
  }
}



// WEBPACK FOOTER //
// ./src/pages/order-navigation/order-navigation.ts